import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, firstValueFrom } from 'rxjs';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { SessionRehydrationService } from 'src/app/shared/services/session-rehydration.service';
import { DealerState, FetchAllDealersAction, UpdateCurrentDealerAction, UpdateDealerFlagsAction } from 'src/app/shared/state/DealerState';
import { FetchAllTranslationsAction } from 'src/app/shared/state/TranslationState';
import { UpdateUserAction } from 'src/app/shared/state/UserState';

import { AuthenticationService } from '../../services/authentication.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { AssetManagementService } from '../../../asset-management/services/asset-management.service';

import { MatDialog } from '@angular/material/dialog';
import { SharedDialogComponent } from 'src/app/shared/components/shared-dialog/shared-dialog.component';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { CookieService } from '../../../../shared/services/cookie.service';
import { mapLanguageCode } from 'src/app/features/user-management/utils/UserSetupUtils';
import { PasswordNotificationService } from 'src/app/shared/services/password-notification.service';
import { TranslationService } from '../../../../shared/translations/services/translation.service';

@Component({
  selector: 'app-auth-login',
  templateUrl: './auth-login.component.html',
  styleUrls: ['./auth-login.component.scss'],
  standalone: false
})
export class AuthLoginComponent implements OnInit {
  private readonly stateManagementService = inject(StateManagementService);
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly sessionRehydrationService = inject(SessionRehydrationService);
  private readonly assetManagementService = inject(AssetManagementService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly featureflagsService = inject(FeatureflagsService);
  private readonly cookieService = inject(CookieService);
  private readonly passwordNotificationService = inject(PasswordNotificationService);
  protected readonly translationService = inject(TranslationService);
  hide = true;
  protected readonly mapLanguageCode = mapLanguageCode;

  dealers$: Observable<Dealer[]>;
  dealerLoading$: Observable<boolean>;
  loginForm: FormGroup;

  sessionExpiredMessage = '';
  loginFailed = false;
  loginProcessing = false;
  loginErrorMessage = '';
  loginAttemptMessage = '';
  chipColor = '';
  pageLoading = true;
  dealerId = this.route.snapshot.queryParams['returnUrl']?.split('/')[3];
  customerId = this.route.snapshot.queryParams['returnUrl']?.split('/')[5];
  passwordDate: Date | null = null;
  currentDate = new Date();

  constructor() {
    this.loginForm = this.fb.group({
      selectedDealer: [null, Validators.required],
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    if (this.authenticationService.isAuthenticated() && !this.authenticationService.isTokenExpired()) {
      this.sessionRehydrationService.restoreDealer().subscribe((dealer) => {
        if (dealer) {
          this.router.navigateByUrl('/home/dashboard');
        } else {
          this.initLoginPage();
        }
      });
      return;
    }
    this.initLoginPage();
  }

  private initLoginPage(): void {
    if (sessionStorage.getItem('session-expired') === 'true') {
      sessionStorage.removeItem('session-expired');
      this.sessionExpiredMessage = 'Your session has expired. Please log in again.';
      setTimeout(() => (this.sessionExpiredMessage = ''), 5000);
    }
    this.clearStorageAndResetStates();
    this.store.dispatch(new FetchAllDealersAction());

    this.dealers$ = this.store.select(DealerState.allDealers);
    this.dealerLoading$ = this.store.select(DealerState.loading);
    // Get the dealerId from the cookie
    this.dealerId = this.cookieService.getCookie('selectedDealer') || this.dealerId;
    this.dealers$.subscribe((value) => {
      value.forEach((value) => {
        // Ensure newHashEnabled has a default value if not provided
        if (!value.newHashEnabled) {
          value.newHashEnabled = 'N';
        }
        if (this.dealerId !== undefined && value.id === this.dealerId) {
          this.loginForm.patchValue({
            selectedDealer: value // Set the default value
          });
        }
      });
    });
  }

  async onSubmit(event: Event) {
    event.preventDefault();
    this.loginFailed = false;
    if (this.loginForm.valid) {
      this.loginProcessing = true;
      const username = this.loginForm.value.username;
      const dealer: Dealer = this.loginForm.value.selectedDealer;
      const encryptedPassword = await this.authenticationService.encryptPassword(this.loginForm.value.password, dealer.newHashEnabled);
      this.authenticationService
        .validateCredentials(username, encryptedPassword, dealer.id, '')
        .then(async (res) => {
          this.passwordDate = new Date(res.password_dt);
          this.loginFailed = false;
          this.store.dispatch(new UpdateCurrentDealerAction(dealer, username));
          this.store.dispatch(new UpdateUserAction(username, res.firstName, res.lastName));
          localStorage.setItem('language', mapLanguageCode(res.language) || 'English (US)');
          const featureFlagRes = await this.featureflagsService.fetchFeatureFlag(dealer.id, FEATURE_FLAGS.ALL);
          this.store.dispatch(new UpdateDealerFlagsAction(featureFlagRes));
          await firstValueFrom(this.store.dispatch(new FetchAllTranslationsAction()));
          const isUserMgmtEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.USER_MGMT_ENABLED);
          if (isUserMgmtEnabled && res.activeFlg === 'N') {
            throw { error: { message: 'INACTIVE_ACCOUNT' } };
          }
          if (isUserMgmtEnabled && res.password_dt !== null && this.passwordDate < this.currentDate) {
            throw { error: { message: 'PASSWORD_EXPIRED' } };
          }
          if (isUserMgmtEnabled && (res.passwordChangeDone === 'N' || res.password_dt === null)) {
            this.dialog
              .open(SharedDialogComponent, {
                data: {
                  title: 'Update your password',
                  message:
                    'You need to update your password because this is the first time you are signing in, or your password has expired.',
                  buttonText: 'Change Password'
                }
              })
              .afterClosed()
              .subscribe((result) => {
                if (result === true) {
                  this.router.navigateByUrl('/home/changepassword', {
                    state: { fromLogin: true }
                  });
                } else {
                  this.router.navigateByUrl('/home/authlogin');
                }
              });
          } else {
            const passwordStatus = await firstValueFrom(this.passwordNotificationService.getPasswordStatus(dealer.id, username)).catch(
              () => null
            );
            if (this.passwordNotificationService.shouldShowAlert(passwordStatus)) {
              const days = this.passwordNotificationService.daysUntilExpiry(passwordStatus.passwordDt);
              const message =
                days > 0
                  ? `${this.translationService.translate('passwordWillExpireText')} ${days} ${this.translationService.translate(
                      'daysText'
                    )}. ${this.translationService.translate('wouldYouLikeToChangeItText')}`
                  : `${this.translationService.translate('passwordExpiresTodayText')} ${this.translationService.translate(
                      'wouldYouLikeToChangeItText'
                    )}`;
              this.passwordNotificationService.putPasswordNotification(dealer.id, username);
              const dialogResult = await firstValueFrom(
                this.dialog
                  .open(SharedDialogComponent, {
                    minWidth: '450px',
                    data: {
                      title: this.translationService.translate('passwordExpiringSoonText'),
                      message,
                      buttonText: this.translationService.translate('changePasswordText'),
                      cancelButtonText: this.translationService.translate('dismissText')
                    }
                  })
                  .afterClosed()
              );
              if (dialogResult === true) {
                this.router.navigateByUrl('/home/changepassword');
                return;
              }
            }
            if (this.customerId !== undefined) {
              this.assetManagementService.getSpecificationLinesFromCustomerId(this.customerId).subscribe((res) => {
                if (res.dataList != null && res.dataList.length > 0) {
                  this.router.navigateByUrl(`/home/specifications/${res.dataList[0].v4SpecificationId}`);
                }
              });
            }
            // Remove the cookie if it exists and set a new one
            this.cookieService.eraseCookie('selectedDealer');
            this.cookieService.setCookie('selectedDealer', dealer.id, 90);
            this.router.navigateByUrl('/home/dashboard');
          }
        })
        .catch((error) => {
          this.loginFailed = true;
          this.loginAttemptMessage = '';
          if (error.error.message === 'ACCOUNT_LOCKED') {
            this.chipColor = 'warn';
            this.loginAttemptMessage =
              'Your account is temporarily locked due to multiple failed logins. Please contact your administrator.';
          } else if (error.error.message === 'INVALID_USERNAME_PASSWORD') {
            this.chipColor = 'accent';
            this.loginErrorMessage = 'Credentials could not be verified';
            const attemptsLeft = error.error.failure_Threshold - error.error.nb_Login_Attempt;
            this.loginAttemptMessage = `You only have ${attemptsLeft} login attempts left before your account is temporarily blocked.`;
          } else if (error.error.message === 'INACTIVE_ACCOUNT') {
            this.chipColor = 'accent';
            this.loginErrorMessage = 'Invalid Credentials';
          } else if (error.error.message === 'PASSWORD_EXPIRED') {
            this.chipColor = 'accent';
            this.loginErrorMessage = 'Your password has expired. Please contact your administrator.';
          } else {
            this.loginErrorMessage = 'Credentials could not be verified';
          }
        })
        .finally(() => {
          this.loginProcessing = false;
        });
    }
  }

  clearStorageAndResetStates() {
    // Preserve column preferences (stored with prefix 'columnPrefs:') across logout/clear
    const preservedPrefix = 'columnPrefs:';
    const preserved: { [key: string]: string | null } = {};

    // collect preserved entries
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(preservedPrefix)) {
        preserved[k] = window.localStorage.getItem(k);
      }
    }

    window.localStorage.clear();
    Object.keys(preserved).forEach((k) => {
      const v = preserved[k];
      if (v !== null) {
        window.localStorage.setItem(k, v);
      }
    });

    this.stateManagementService.resetAllStates();
  }
}
